// The Phone view toggle (Session BDZ; a true on/off toggle since Session BEA). Shown to an admin on a screen wider than 900px, and
// nowhere else: not on a real phone, not to the office role, an engineer, an auditor or a customer, and not inside the Phone view's
// own frame. One press turns the Phone view on; the next turns it off. While it is on the icon is in its active state and the button
// says so to assistive technology (aria-pressed), the way the theme toggle shows its own state. The stage sits BELOW the app header,
// so this control is still on screen, and clickable, while the Phone view is on.
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Smartphone } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { isInPhonePreviewFrame, normalisePhonePath, phoneViewAvailable } from "../../lib/phonePreview";
import { togglePhoneView, usePhoneViewState } from "../../lib/phoneViewStore";

/** The window's width, kept current. A resize past the 900px line shows or hides the control. */
export function useViewportWidth(): number {
  const [width, setWidth] = useState(() => (typeof window === "undefined" ? 0 : window.innerWidth));
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return width;
}

export function PhoneViewButton() {
  const { user } = useAuth();
  const location = useLocation();
  const viewportWidth = useViewportWidth();
  const { open } = usePhoneViewState();
  if (!phoneViewAvailable({ role: user?.role, viewportWidth, inFrame: isInPhonePreviewFrame() })) return null;

  const press = () => {
    // Turning it on opens at the current page, when it is one the frame can open; otherwise the office home.
    const here = normalisePhonePath(`${location.pathname}${location.search}`, window.location.origin);
    togglePhoneView(here ?? "/office");
  };

  return (
    <button
      type="button"
      onClick={press}
      aria-label="Phone view"
      aria-pressed={open}
      title={open ? "Phone view is on: press to leave it" : "Phone view: see this page at phone size"}
      className={`flex h-11 flex-none items-center justify-center gap-1.5 rounded-ele px-2.5 outline-none transition focus-visible:ring-2 focus-visible:ring-ele-purple ${
        open ? "bg-ele-row-hover text-ele-purple" : "text-ele-muted hover:text-ele-strong"
      }`}
    >
      <Smartphone aria-hidden size={20} strokeWidth={open ? 2.5 : 2} />
      <span className="hidden text-sm lg:inline">Phone view</span>
    </button>
  );
}
